/**
 * Impressão da evolução clínica: evolução, indicações e cada impresso em páginas separadas.
 */

import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { resolveEvolutionEntryFormat } from './clinicEvolutionFormat';
import { sanitizeClinicalHtml } from './clinicalHtml';
import {
  buildCertificatePrintHtml,
  buildCidConsentBlockedNoticeHtml,
  isCidConsentRequiredError,
} from './certificatePrintDocument';
import { buildExamOrderPrintHtml } from './examOrderPrintDocument';
import { buildEvolutionTextDocBodyHtml } from './evolutionTextDocPrintDocument';
import {
  buildSignedPrescriptionNoticeHtml,
  resolvePrescriptionPrintPart,
} from './evolutionPrescriptionPrint';
import {
  buildSignedMedicalReportNoticeHtml,
  resolveMedicalReportPrintPart,
} from './evolutionMedicalReportPrint';
import { openSignedPrescriptionPdf, openSignedMedicalReportPdf } from '../services/documentSignatureService';
import type { EvolutionPrintableCategory, EvolutionAttachedPrintable } from '../components/evolution/evolutionWorkflowTypes';
import {
  formatEvolutionReturnWorkflowLines,
  getEvolutionIndicationAttachment,
  getEvolutionNonIndicationPrintables,
  getEvolutionReturnWorkflow,
  normalizeEvolutionProcedureLabels,
} from '../components/evolution/evolutionViewHelpers';
import {
  buildClinicMultiSheetPrintHtml,
  buildClinicPrintDocumentHtml,
  buildEvolutionPrintAttachmentSection,
  buildClinicalPrintPatientInfoHtml,
  escapeHtml,
  type ClinicPrintSheetSpec,
} from './clinicPrintLetterhead';
import { printClinicHtml } from './clinicPrintHtml';
import { formatEvolutionAdditionDate } from './evolutionAdditionPolicy';

function extractBodyInner(fullHtml: string) {
  const m = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  return m ? m[1].trim() : fullHtml;
}

function nl2brClinical(t: string) {
  return sanitizeClinicalHtml(String(t || '').replace(/\r\n|\r|\n/g, '<br/>'));
}

function evolutionCoreHasContent(evo: any): boolean {
  const vs = evo?.vital_signs || {};
  if (Object.values(vs).some((v) => v !== undefined && v !== null && v !== '')) return true;
  if (String(evo?.evolution_free_text || '').trim()) return true;
  if (String(evo?.subjective || '').trim() || String(evo?.objective || '').trim()) return true;
  if (String(evo?.assessment || '').trim() || String(evo?.plan || '').trim()) return true;
  if (String(evo?.diagnosis || '').trim() || String(evo?.internal_notes || '').trim()) return true;
  if (formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(evo)).length > 0) return true;
  if (String(evo?.follow_up_notes || '').trim() || evo?.follow_up_date) return true;
  if (Array.isArray(evo?.additions) && evo.additions.length > 0) return true;
  return false;
}

function buildEvolutionAdditionsPrintHtml(evo: any): string {
  const list = Array.isArray(evo?.additions) ? evo.additions : [];
  if (!list.length) return '';
  const blocks = list
    .map((row: any) => {
      const when = formatEvolutionAdditionDate(row.added_at);
      const who = row.professional?.full_name ? ` — ${row.professional.full_name}` : '';
      const body = sanitizeClinicalHtml(String(row.body_html || ''));
      const docs = Array.isArray(row.documents) ? row.documents : [];
      const imgs = Array.isArray(row.images) ? row.images : [];
      const attachLines = [
        ...docs.map((d: any) => {
          const label = escapeHtml(String(d.title || d.file_name || 'Documento'));
          const url = d.file_url ? escapeHtml(String(d.file_url)) : '';
          return url ? `<li>${label} — <a href="${url}">${url}</a></li>` : `<li>${label}</li>`;
        }),
        ...imgs.map((d: any) => {
          const label = escapeHtml(String(d.title || d.file_name || 'Imagem'));
          const url = d.file_url ? escapeHtml(String(d.file_url)) : '';
          return url ? `<li>${label} — <a href="${url}">${url}</a></li>` : `<li>${label}</li>`;
        }),
      ];
      const attachHtml = attachLines.length
        ? `<ul style="margin:0.5rem 0 0 1rem;padding:0;font-size:11px;color:#4b5563">${attachLines.join('')}</ul>`
        : '';
      return `<div style="margin-bottom:1rem;padding-bottom:0.75rem;border-bottom:1px dashed #e5e7eb">
        <div style="font-size:11px;font-weight:700;color:#6b7280;margin-bottom:0.35rem">Informação adicionada em ${escapeHtml(when)}${escapeHtml(who)}</div>
        <div class="attachment-body">${body}</div>${attachHtml}
      </div>`;
    })
    .join('');
  return `<div class="section"><div class="section-title">Informações adicionadas</div>${blocks}</div>`;
}

function buildIndicationsBodyHtml(evo: any): string {
  const procLabels = normalizeEvolutionProcedureLabels(evo);
  const indicationAttachment = getEvolutionIndicationAttachment(evo);
  const indicationBody = String(indicationAttachment?.bodyText || '').trim();
  if (!indicationBody && procLabels.length === 0) return '';

  if (indicationBody) {
    return `<pre style="margin:0;white-space:pre-wrap;font-family:inherit;font-size:12px;line-height:1.55">${escapeHtml(indicationBody)}</pre>`;
  }

  return `<ul style="margin:0 0 0.75rem 1.25rem;padding-left:0">${procLabels.map((n) => `<li>${escapeHtml(n)}</li>`).join('')}</ul>`;
}

export type ClinicalEvolutionPrintOptions = {
  /** Texto/registro clínico (SOAP, texto livre, sinais vitais, diagnóstico, retorno). */
  includeEvolutionCore?: boolean;
  /** Indicações / procedimentos da evolução. */
  includeIndications?: boolean;
  /** Receituários, exames, atestados e demais anexos vinculados. */
  includePrintables?: boolean;
  /** Evita nova requisição ao exportar várias evoluções em sequência. */
  clinic?: any | null;
};

async function fetchClinicPrintSettings(): Promise<any | null> {
  try {
    const { data } = await axios.get(getApiUrl('/clinic/settings'));
    return data?.data ?? null;
  } catch {
    return null;
  }
}

function compareEvolutionsChronologically(a: any, b: any): number {
  const da = String(a?.evolution_date || '');
  const db = String(b?.evolution_date || '');
  if (da !== db) return da.localeCompare(db);
  const ta = String(a?.evolution_time || '');
  const tb = String(b?.evolution_time || '');
  if (ta !== tb) return ta.localeCompare(tb);
  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

function buildPatientEvolutionsExportCoverHtml(patient: any, evolutions: any[]): string {
  const patientBlock = buildClinicalPrintPatientInfoHtml(patient, {
    variant: 'detailed',
    issueDate: new Date(),
  });
  const generatedAt = new Date().toLocaleString('pt-BR');
  const indexRows = evolutions
    .map((evo, i) => {
      const date = evo.evolution_date
        ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR')
        : '-';
      const time = evo.evolution_time ? String(evo.evolution_time).slice(0, 5) : '';
      const prof = evo.professional?.full_name || '-';
      return `<tr>
        <td style="padding:0.35rem 0.5rem;border-bottom:1px solid #e5e7eb;text-align:center;width:2rem">${i + 1}</td>
        <td style="padding:0.35rem 0.5rem;border-bottom:1px solid #e5e7eb">${escapeHtml(date)}${time ? ` ${escapeHtml(time)}` : ''}</td>
        <td style="padding:0.35rem 0.5rem;border-bottom:1px solid #e5e7eb">${escapeHtml(prof)}</td>
      </tr>`;
    })
    .join('');
  return `
<div class="doc-badge"><span>Prontuário</span></div>
<h1 class="doc-title">Histórico de evoluções clínicas</h1>
${patientBlock}
<p style="margin:1rem 0 0.35rem;font-size:12px;color:#475569"><strong>Total de registros:</strong> ${evolutions.length}</p>
<p style="margin:0 0 1rem;font-size:12px;color:#475569"><strong>Documento gerado em:</strong> ${escapeHtml(generatedAt)}</p>
${
  indexRows
    ? `<div class="section"><div class="section-title">Índice</div>
<table style="width:100%;border-collapse:collapse;font-size:11px">
  <thead><tr>
    <th style="text-align:left;padding:0.35rem 0.5rem;border-bottom:2px solid #cbd5e1">#</th>
    <th style="text-align:left;padding:0.35rem 0.5rem;border-bottom:2px solid #cbd5e1">Data</th>
    <th style="text-align:left;padding:0.35rem 0.5rem;border-bottom:2px solid #cbd5e1">Profissional</th>
  </tr></thead>
  <tbody>${indexRows}</tbody>
</table></div>`
    : ''
}`;
}

/** Folhas de impressão de uma evolução (sem wrapper HTML completo). */
export async function buildClinicalEvolutionPrintSheets(
  evo: any,
  options?: ClinicalEvolutionPrintOptions,
): Promise<{
  sheets: ClinicPrintSheetSpec[];
  signedPrescriptions: any[];
  signedMedicalReports: any[];
  clinic: any | null;
  subtitleBelowBadge?: string;
  emptyDocument?: true;
}> {
  const includeEvolutionCore = options?.includeEvolutionCore !== false;
  const includeIndications = options?.includeIndications !== false;
  const includePrintables = options?.includePrintables !== false;
  const signedPrescriptions: any[] = [];
  const signedMedicalReports: any[] = [];
  const clinic = options?.clinic !== undefined ? options.clinic : await fetchClinicPrintSettings();

  const vs = evo.vital_signs || {};
  const hasVitals = Object.values(vs).some((v) => v);
  const useFreeText = resolveEvolutionEntryFormat(evo) === 'free_text';

  const profSigSrc = String(evo?.professional?.signature_image_url ?? '').trim();
  const profSigImg = profSigSrc
    ? `<div class="prof-sig-print"><img src="${escapeHtml(profSigSrc)}" alt="" /></div>`
    : '';

  const evolutionFreePrintHtml =
    useFreeText && evo.evolution_free_text
      ? sanitizeClinicalHtml(String(evo.evolution_free_text).replace(/\r\n|\r|\n/g, '<br/>'))
      : '';

  const evolutionDateFormatted = evo.evolution_date
    ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR')
    : '-';
  const timeShort = evo.evolution_time ? String(evo.evolution_time).slice(0, 5) : '';
  const subtitleBelowBadge = `Data: ${evolutionDateFormatted}${timeShort ? ` às ${timeShort}` : ''}`;

  const patientBlock = buildClinicalPrintPatientInfoHtml(evo.patient, { variant: 'compact' });
  const indicationPatientBlock = buildClinicalPrintPatientInfoHtml(evo.patient, {
    variant: 'detailed',
    issueDate: evo.evolution_date || new Date(),
  });

  const printableCatLab: Record<EvolutionPrintableCategory, string> = {
    prescription: 'Receituário',
    certificate: 'Atestado',
    exam_order: 'Exames',
    refraction: 'Refração',
    indication: 'Indicações',
    medical_report: 'Relatório médico',
    report: 'Laudo',
    surgical_risk: 'Risco cirúrgico',
    insurance_guide: 'Guia de convênio',
  };

  const renderTextDocFallback = (p: { category: string; templateName: string; bodyText?: string }) =>
    buildEvolutionTextDocBodyHtml({
      category: p.category,
      templateName: p.templateName,
      bodyText: String(p.bodyText || ''),
      patient: evo.patient,
      professional: evo.professional,
      issueDate: evo.evolution_date,
    });

  const renderPrintableAttachment = async (p: EvolutionAttachedPrintable): Promise<string> => {
    if (p.linkedDocumentId && p.linkedDocumentType === 'prescription') {
      const resolved = await resolvePrescriptionPrintPart(p.linkedDocumentId, extractBodyInner);
      if (resolved?.kind === 'signed') {
        signedPrescriptions.push(resolved.prescription);
        return buildSignedPrescriptionNoticeHtml(resolved.prescription);
      }
      if (resolved?.kind === 'html') return resolved.html;
      return renderTextDocFallback(p);
    }
    if (p.linkedDocumentId && p.linkedDocumentType === 'certificate') {
      try {
        const { data } = await axios.get(getApiUrl(`/clinic/certificates/${p.linkedDocumentId}/print`));
        const printData = data?.data ?? data;
        return `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildCertificatePrintHtml(printData))}</div>`;
      } catch (e) {
        if (isCidConsentRequiredError(e)) return buildCidConsentBlockedNoticeHtml();
        return renderTextDocFallback(p);
      }
    }
    if (p.linkedDocumentId && p.linkedDocumentType === 'medical_report') {
      const resolved = await resolveMedicalReportPrintPart(p.linkedDocumentId, extractBodyInner);
      if (resolved?.kind === 'signed') {
        signedMedicalReports.push(resolved.report);
        return buildSignedMedicalReportNoticeHtml(resolved.report);
      }
      if (resolved?.kind === 'html') return resolved.html;
      return renderTextDocFallback(p);
    }
    if (p.linkedDocumentId && p.linkedDocumentType === 'exam_order') {
      try {
        const { data } = await axios.get(getApiUrl(`/clinic/exam-orders/${p.linkedDocumentId}/print`));
        const printData = data?.data ?? data;
        return `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildExamOrderPrintHtml(printData))}</div>`;
      } catch {
        return renderTextDocFallback(p);
      }
    }
    if (['exam_order', 'refraction', 'report', 'surgical_risk', 'insurance_guide', 'indication'].includes(p.category)) {
      return renderTextDocFallback(p);
    }
    const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
    const cat = printableCatLab[p.category] || p.category;
    return buildEvolutionPrintAttachmentSection(cat, p.templateName, body);
  };

  const profCredentials: string[] = Array.isArray(evo.professional?.professional_credentials)
    ? evo.professional.professional_credentials
    : [];
  const credentialsHtml = profCredentials
    .filter(Boolean)
    .map((c) => `<span style="display:block;font-style:italic;font-size:11px;color:#374151;">${escapeHtml(c)}</span>`)
    .join('');
  const footerBlock = `
<div class="sheet-evolution-footer">
  ${profSigImg}
  <div class="sheet-evolution-signature-rule"></div>
  <strong>${escapeHtml(evo.professional?.full_name || '-')}</strong>
  ${credentialsHtml}
  <span style="display:block;">${escapeHtml(evo.professional?.specialty || '')}</span>
  <span style="display:block;">${escapeHtml(`${evo.professional?.council_type || 'CRM'} ${evo.professional?.council_number || ''}${evo.professional?.council_state ? ` - ${evo.professional.council_state}` : ''}`)}</span>
  ${evo.is_signed ? `<small style="color:#22c55e;">✓ Assinado digitalmente em ${escapeHtml(evo.signed_at ? new Date(evo.signed_at).toLocaleString('pt-BR') : '')}</small>` : ''}
</div>`;

  const wfLines = formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(evo));
  let returnInner = '';
  if (wfLines.length) {
    returnInner += wfLines.map((l) => `<p style="margin:0 0 0.35em 0">• ${escapeHtml(l)}</p>`).join('');
  }
  if (evo.follow_up_notes && String(evo.follow_up_notes).trim()) {
    returnInner += `<p style="margin:0.5em 0 0 0">${nl2brClinical(evo.follow_up_notes)}</p>`;
  }
  if (evo.follow_up_date) {
    returnInner += `<p style="margin:0.35em 0 0 0"><strong>Data de retorno:</strong> ${escapeHtml(new Date(evo.follow_up_date + 'T12:00:00').toLocaleDateString('pt-BR'))}</p>`;
  }
  const retSection =
    returnInner.trim() !== ''
      ? `<div class="section"><div class="section-title">Retorno</div><div class="attachment-body">${returnInner}</div></div>`
      : '';

  const sheets: { bodyHtml: string }[] = [];

  if (includeEvolutionCore && evolutionCoreHasContent(evo)) {
    const evolutionBody = `
<div class="doc-badge"><span>Evolução clínica</span></div>
<div class="doc-number">${escapeHtml(subtitleBelowBadge)}</div>
<h1 class="doc-title">Registro de atendimento</h1>
${patientBlock}
${hasVitals ? `<div class="section">
  <div class="section-title">Sinais Vitais</div>
  <div class="vitals-grid">
    ${vs.blood_pressure_systolic ? `<div class="vital-item"><div class="vital-value">${vs.blood_pressure_systolic}/${vs.blood_pressure_diastolic || '-'}</div><div class="vital-label">PA (mmHg)</div></div>` : ''}
    ${vs.heart_rate ? `<div class="vital-item"><div class="vital-value">${vs.heart_rate}</div><div class="vital-label">FC (bpm)</div></div>` : ''}
    ${vs.temperature ? `<div class="vital-item"><div class="vital-value">${vs.temperature}°C</div><div class="vital-label">Temperatura</div></div>` : ''}
    ${vs.respiratory_rate ? `<div class="vital-item"><div class="vital-value">${vs.respiratory_rate}</div><div class="vital-label">FR (irpm)</div></div>` : ''}
    ${vs.oxygen_saturation ? `<div class="vital-item"><div class="vital-value">${vs.oxygen_saturation}%</div><div class="vital-label">SpO₂</div></div>` : ''}
    ${vs.weight ? `<div class="vital-item"><div class="vital-value">${vs.weight} kg</div><div class="vital-label">Peso</div></div>` : ''}
    ${vs.height ? `<div class="vital-item"><div class="vital-value">${vs.height} cm</div><div class="vital-label">Altura</div></div>` : ''}
    ${vs.bmi ? `<div class="vital-item"><div class="vital-value">${vs.bmi}</div><div class="vital-label">IMC</div></div>` : ''}
    ${vs.blood_glucose ? `<div class="vital-item"><div class="vital-value">${vs.blood_glucose}</div><div class="vital-label">Glicemia (mg/dL)</div></div>` : ''}
    ${vs.pain_scale !== undefined && vs.pain_scale !== null ? `<div class="vital-item"><div class="vital-value">${vs.pain_scale}/10</div><div class="vital-label">Dor</div></div>` : ''}
  </div>
</div>` : ''}
${evolutionFreePrintHtml ? `<div class="section"><div class="section-title">Evolução (texto livre)</div><div class="attachment-body">${evolutionFreePrintHtml}</div></div>` : ''}
${!useFreeText && evo.subjective ? `<div class="section"><div class="section-title"><span class="soap-label">S</span> Subjetivo</div><div class="attachment-body"><p>${nl2brClinical(evo.subjective)}</p></div></div>` : ''}
${!useFreeText && evo.objective ? `<div class="section"><div class="section-title"><span class="soap-label">O</span> Objetivo</div><div class="attachment-body"><p>${nl2brClinical(evo.objective)}</p></div></div>` : ''}
${!useFreeText && evo.assessment ? `<div class="section"><div class="section-title"><span class="soap-label">A</span> Avaliação</div><div class="attachment-body"><p>${nl2brClinical(evo.assessment)}</p></div></div>` : ''}
${!useFreeText && evo.plan ? `<div class="section"><div class="section-title"><span class="soap-label">P</span> Plano</div><div class="attachment-body"><p>${nl2brClinical(evo.plan)}</p></div></div>` : ''}
${evo.diagnosis ? `<div class="section"><div class="section-title">Diagnóstico</div><div class="attachment-body"><p>${nl2brClinical(evo.diagnosis)}${evo.diagnosis_code ? ` (CID: ${escapeHtml(String(evo.diagnosis_code))})` : ''}</p></div></div>` : ''}
${evo.internal_notes && String(evo.internal_notes).trim() ? `<div class="section"><div class="section-title">Observações internas</div><div class="attachment-body"><p>${nl2brClinical(evo.internal_notes)}</p></div></div>` : ''}
${retSection}
${buildEvolutionAdditionsPrintHtml(evo)}
${footerBlock}`;
    sheets.push({ bodyHtml: evolutionBody });
  }

  const indicationsInner = includeIndications ? buildIndicationsBodyHtml(evo) : '';
  if (indicationsInner) {
    const indicationAttachment = getEvolutionIndicationAttachment(evo);
    const indicationTitle = indicationAttachment?.templateName || 'Indicações — cirurgias e procedimentos';
    sheets.push({
      bodyHtml: `
<div class="doc-badge"><span>Indicações</span></div>
${indicationPatientBlock}
<div class="section"><div class="section-title">${escapeHtml(indicationTitle)}</div><div class="attachment-body">${indicationsInner}</div></div>
${footerBlock}`,
    });
  }

  if (includePrintables) {
    for (const p of getEvolutionNonIndicationPrintables(evo)) {
      sheets.push({ bodyHtml: await renderPrintableAttachment(p) });
    }
  }

  if (sheets.length === 0) {
    return {
      sheets: [{ bodyHtml: `${patientBlock}<p>Nenhum conteúdo para imprimir.</p>` }],
      signedPrescriptions,
      signedMedicalReports,
      clinic,
      subtitleBelowBadge,
      emptyDocument: true as const,
    };
  }

  return {
    sheets,
    signedPrescriptions,
    signedMedicalReports,
    clinic,
    subtitleBelowBadge,
  };
}

function wrapEvolutionPrintSheetsAsHtml(result: {
  sheets: ClinicPrintSheetSpec[];
  clinic: any | null;
  subtitleBelowBadge?: string;
  emptyDocument?: true;
}): string {
  const { sheets, clinic, subtitleBelowBadge, emptyDocument } = result;
  if (emptyDocument) {
    return buildClinicPrintDocumentHtml({
      clinic,
      browserTitle: 'Evolução Clínica',
      badgeText: 'Evolução clínica',
      subtitleBelowBadge,
      bodyHtml: sheets[0]?.bodyHtml || '',
    });
  }
  if (sheets.length === 1) {
    const bodyHtml = sheets[0].bodyHtml;
    const bodyHasDocBadge = bodyHtml.includes('doc-badge');
    return buildClinicPrintDocumentHtml({
      clinic,
      browserTitle: 'Evolução Clínica',
      badgeText: bodyHasDocBadge ? undefined : 'Evolução clínica',
      subtitleBelowBadge: bodyHasDocBadge ? undefined : subtitleBelowBadge,
      bodyHtml,
    });
  }
  return buildClinicMultiSheetPrintHtml({
    clinic,
    browserTitle: 'Evolução Clínica',
    sheets,
  });
}

/** Gera HTML multi-página: evolução · indicações · cada impresso/solicitação. */
export async function buildClinicalEvolutionMultiSheetPrintHtml(
  evo: any,
  options?: ClinicalEvolutionPrintOptions,
): Promise<{ html: string; signedPrescriptions: any[]; signedMedicalReports: any[] }> {
  const built = await buildClinicalEvolutionPrintSheets(evo, options);
  return {
    html: wrapEvolutionPrintSheetsAsHtml(built),
    signedPrescriptions: built.signedPrescriptions,
    signedMedicalReports: built.signedMedicalReports,
  };
}

/** Um único PDF/HTML com todas as evoluções do paciente (registro clínico, timbrado). */
export async function buildPatientClinicalEvolutionsExportHtml(
  patient: any,
  evolutions: any[],
): Promise<string> {
  const sorted = [...evolutions].sort(compareEvolutionsChronologically);
  let clinic = await fetchClinicPrintSettings();
  const exportSheets: ClinicPrintSheetSpec[] = [
    { bodyHtml: buildPatientEvolutionsExportCoverHtml(patient, sorted) },
  ];

  for (const evo of sorted) {
    const merged = { ...evo, patient: evo.patient || patient };
    if (!evolutionCoreHasContent(merged)) continue;
    const { sheets, clinic: loadedClinic } = await buildClinicalEvolutionPrintSheets(merged, {
      includeEvolutionCore: true,
      includeIndications: false,
      includePrintables: false,
      clinic,
    });
    clinic = loadedClinic;
    exportSheets.push(...sheets);
  }

  if (exportSheets.length === 1) {
    exportSheets.push({
      bodyHtml: `${buildClinicalPrintPatientInfoHtml(patient, { variant: 'compact' })}<p>Nenhuma evolução com conteúdo clínico para exportar.</p>`,
    });
  }

  const patientName = String(patient?.full_name || 'Paciente').trim();
  return buildClinicMultiSheetPrintHtml({
    clinic,
    browserTitle: `Evoluções — ${patientName}`,
    sheets: exportSheets,
  });
}

/** Abre o diálogo de impressão / Salvar como PDF com o histórico completo de evoluções. */
export async function printPatientClinicalEvolutionsExport(patient: any, evolutions: any[]) {
  if (!evolutions?.length) return;
  const patientName = String(patient?.full_name || 'Paciente').trim();
  const html = await buildPatientClinicalEvolutionsExportHtml(patient, evolutions);
  printClinicHtml(html, { title: `Evoluções — ${patientName}` });
}

/** Folha única legada — preferir multi-página via printClinicalEvolutionFullDocument. */
export async function buildClinicalEvolutionFullPrintHtml(evo: any): Promise<string> {
  const { html } = await buildClinicalEvolutionMultiSheetPrintHtml(evo);
  return html;
}

export async function printClinicalEvolutionFullDocument(evo: any) {
  if (!evo) return;
  const { html, signedPrescriptions, signedMedicalReports } = await buildClinicalEvolutionMultiSheetPrintHtml(evo, {
    includeEvolutionCore: true,
  });
  printClinicHtml(html);
  for (const rx of signedPrescriptions) {
    await openSignedPrescriptionPdf(rx.id, rx.signed_pdf_url).catch(() => undefined);
  }
  for (const report of signedMedicalReports) {
    await openSignedMedicalReportPdf(report.id, report.signed_pdf_url).catch(() => undefined);
  }
}

/** Apenas o registro clínico da evolução (sem indicações, receituários nem solicitações). */
export async function printClinicalEvolutionTextOnlyDocument(evo: any) {
  if (!evo) return;
  const { html } = await buildClinicalEvolutionMultiSheetPrintHtml(evo, {
    includeEvolutionCore: true,
    includeIndications: false,
    includePrintables: false,
  });
  printClinicHtml(html);
}

/** Imprime apenas impressos, solicitações e indicações — sem o texto/registro da evolução. */
export async function printClinicalEvolutionBatchDocument(evo: any) {
  if (!evo) return;
  const { html, signedPrescriptions, signedMedicalReports } = await buildClinicalEvolutionMultiSheetPrintHtml(evo, {
    includeEvolutionCore: false,
  });
  printClinicHtml(html);
  for (const rx of signedPrescriptions) {
    await openSignedPrescriptionPdf(rx.id, rx.signed_pdf_url).catch(() => undefined);
  }
  for (const report of signedMedicalReports) {
    await openSignedMedicalReportPdf(report.id, report.signed_pdf_url).catch(() => undefined);
  }
}
